import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentHistory from './pages/StudentHistory';
import StaffHistory from './pages/StaffHistory';
import Teachers from './pages/Teachers';
import Classes from './pages/Classes';
import Fees from './pages/Fees';
import Attendance from './pages/Attendance';
import SalaryRecords from './pages/SalaryRecords';
import LeaveManagement from './pages/LeaveManagement';
import StudentLayout from './components/Layout/StudentLayout';
import StudentDashboard from './pages/StudentDashboard';
import {
  StudentAttendance, StudentResults, StudentGradeReport, StudentFees, StudentTimetable,
  StudentMaterials, StudentNotices, StudentDatesheets, StudentProfile,
} from './pages/StudentPages';
import BiometricSettings from './pages/BiometricSettings';
import TeacherAttendancePage from './pages/TeacherAttendancePage';
import Timetable from './pages/Timetable';
import TeacherTimetable from './pages/TeacherTimetable';
import CoordinatorTimetable from './pages/CoordinatorTimetable';
import AssessmentTypes from './pages/AssessmentTypes';
import TermPeriods from './pages/TermPeriods';
import TermSubjectSplit from './pages/TermSubjectSplit';
import AdminTermExams from './pages/AdminTermExams';
import AdminAssessments from './pages/AdminAssessments';
import CLOManagement      from './pages/CLOManagement';
import PLOManagement      from './pages/PLOManagement';
import PLOAttainmentDashboard from './pages/PLOAttainmentDashboard';
import AdminFeedback          from './pages/AdminFeedback';
import ClassPromotion         from './pages/ClassPromotion';
import YearEndCleanup         from './pages/YearEndCleanup';
import StudentFeedback        from './pages/StudentFeedback';
import TeacherFeedbackResults from './pages/TeacherFeedbackResults';
import ParentLayout    from './components/Layout/ParentLayout';
import ParentDashboard from './pages/ParentDashboard';
import {
  ParentAttendance, ParentResults, ParentGradeReport, ParentFees, ParentMaterials,
  ParentNotices, ParentTimetable, ParentDatesheets, ParentQuizzes,
  ParentFeedback, ParentProfile, ParentMessages,
} from './pages/ParentPages';
import AttainmentDashboard from './pages/AttainmentDashboard';
import AdminStudentAttendance from './pages/AdminStudentAttendance';
import AdminDatesheet from './pages/AdminDatesheet';
import TeacherAssessments from './pages/TeacherAssessments';
import TeacherTermExams from './pages/TeacherTermExams';
import GradeReport from './pages/GradeReport';
import TeacherStudentAttendance from './pages/TeacherStudentAttendance';
import TeacherStudentLeaves from './pages/TeacherStudentLeaves';
import TeacherDatesheet from './pages/TeacherDatesheet';
import TeacherLayout from './components/Layout/TeacherLayout';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherAttendance from './pages/TeacherAttendance';
import TeacherSalary from './pages/TeacherSalary';
import TeacherLeaves from './pages/TeacherLeaves';
import TeacherClasses from './pages/TeacherClasses';
import TeacherQuiz from './pages/TeacherQuiz';
import { TeacherExams, TeacherMaterials, TeacherNotices, TeacherProfile, TeacherMessages } from './pages/TeacherPages';
import TeacherClassMedia from './pages/TeacherClassMedia';
import ParentClassMedia from './pages/ParentClassMedia';
import StudentQuiz from './pages/StudentQuiz';
import TeacherSalaryYear from './pages/TeacherSalaryYear';
import TeacherSalaryMonth from './pages/TeacherSalaryMonth';
import Exams from './pages/Exams';
import Materials from './pages/Materials';
import Notices from './pages/Notices';
import Settings from './pages/Settings';
import Expenses from './pages/Expenses';
import Assets from './pages/Assets';
import { createCertificatesPage } from './pages/Certificates';
import { AccessProvider } from './hooks/useAccess';
import CoordinatorLayout from './components/Layout/CoordinatorLayout';
import CoordinatorRequestsAdmin from './pages/CoordinatorRequestsAdmin';
import {
  CoordinatorDashboard, CoordinatorTeacherAttendance,
  CoordinatorNotices, CoordinatorRequests, CoordinatorProfile,
} from './pages/CoordinatorPages';
import CoordStudents from './pages/CoordStudents';
import CoordTeachers from './pages/CoordTeachers';
import CoordParentManagement from './pages/CoordParentManagement';
import CoordClasses from './pages/CoordClasses';
import CoordSalary from './pages/CoordSalary';
import CoordStudentAttendance from './pages/CoordStudentAttendance';
import CoordAssessments     from './pages/CoordAssessments';
import CoordCLOManagement  from './pages/CoordCLOManagement';
import CoordAssessmentTypes from './pages/CoordAssessmentTypes';
import CoordMaterials from './pages/CoordMaterials';
import CoordGradeReport from './pages/CoordGradeReport';
import CoordSalaryYear from './pages/CoordSalaryYear';
import CoordSalaryMonth from './pages/CoordSalaryMonth';
import CoordAccessPanel from './pages/CoordAccessPanel';
import AccessPanel from './pages/AccessPanel';
import AccessGuard from './pages/AccessGuard';
import StudentLeft  from './pages/StudentLeft';
import StaffExits from './pages/StaffExits';

// Finance pages
import FinanceLayout from './components/Layout/FinanceLayout';
import { FinanceDashboard, FinanceRequests, FinanceNotices } from './pages/FinancePages';
import FinanceFees        from './pages/FinanceFees';
import FinanceSalary      from './pages/FinanceSalary';
import FinanceSalaryYear  from './pages/FinanceSalaryYear';
import FinanceSalaryMonth from './pages/FinanceSalaryMonth';
import FinanceExpenses    from './pages/FinanceExpenses';
import FinanceAssets      from './pages/FinanceAssets';
import FinanceProfile     from './pages/FinanceProfile';
import FinanceRequestsAdmin from './pages/FinanceRequestsAdmin';
import FinanceAttendance  from './pages/FinanceAttendance';
import FinanceLeave            from './pages/FinanceLeave';
import FinanceStaffAttendance from './pages/FinanceStaffAttendance';
import FinanceStaffLeaves     from './pages/FinanceStaffLeaves';
import FinanceStaffManagement from './pages/FinanceStaffManagement';
import FinanceMySalary        from './pages/FinanceMySalary';

// Owner pages
import OwnerLayout    from './components/Layout/OwnerLayout';
import OwnerDashboard from './pages/OwnerDashboard';
import OwnerBranches  from './pages/OwnerBranches';
import OwnerAdminAccess from './pages/OwnerAdminAccess';
import OwnerAddBranch         from './pages/OwnerAddBranch';
import OwnerAdminAttendance   from './pages/OwnerAdminAttendance';
import OwnerAdminSalary       from './pages/OwnerAdminSalary';
import OwnerAdminSalaryYear   from './pages/OwnerAdminSalaryYear';
import OwnerAdminSalaryMonth  from './pages/OwnerAdminSalaryMonth';
import OwnerAdminLeaves       from './pages/OwnerAdminLeaves';
import OwnerNotices           from './pages/OwnerNotices';
import AdminAttendance        from './pages/AdminAttendance';
import AdminSalary            from './pages/AdminSalary';
import AdminLeaves            from './pages/AdminLeaves';

// Super admin layout + pages
import SuperLayout from './components/Layout/SuperLayout';
import SuperSchools from './pages/super/SuperSchools';
import SuperSchoolDetail from './pages/super/SuperSchoolDetail';
import SuperDashboard from './pages/super/SuperDashboard';
import SuperStudents from './pages/super/SuperStudents';
import SuperTeachers from './pages/super/SuperTeachers';
import SuperAdmins from './pages/super/SuperAdmins';
import SuperOwners        from './pages/super/SuperOwners';
import SuperStaff         from './pages/super/SuperStaff';
import SuperSubscriptions from './pages/super/SuperSubscriptions';
import SuperServices      from './pages/super/SuperServices';
import SuperBilling       from './pages/super/SuperBilling';
import SuperBranchRequests from './pages/super/SuperBranchRequests';
import StaffAttendance   from './pages/StaffAttendance';
import StaffSalary       from './pages/StaffSalary';
import StaffLeaves       from './pages/StaffLeaves';
import StaffManagement   from './pages/StaffManagement';
import Holidays          from './pages/Holidays';
import StaffSalaryYear    from './pages/StaffSalaryYear';
import StaffSalaryMonth   from './pages/StaffSalaryMonth';
import StudentGateLog from './pages/StudentGateLog';
import FinanceBiometric from './pages/FinanceBiometric';
import FinanceStudentGateLog from './pages/FinanceStudentGateLog';

// ── Coordinator new pages ─────────────────────────────────────────────────────
import CoordOwnAttendance    from './pages/CoordOwnAttendance';
import CoordOwnLeave         from './pages/CoordOwnLeave';
import CoordHolidays         from './pages/CoordHolidays';
import CoordStudentLeft      from './pages/CoordStudentLeft';
import CoordStaffExits       from './pages/CoordStaffExits';

// ── ILP pages ─────────────────────────────────────────────────────────────────
import TeacherILP        from './pages/TeacherILP';
import AdminILP          from './pages/AdminILP';
import ParentILP         from './pages/ParentILP';
import ParentGateLog     from './pages/ParentGateLog';

// ── New pages (exit management + parent portal) ───────────────────────────────
import CoordinatorsAdmin      from './pages/CoordinatorsAdmin';
import CoordTermPeriods       from './pages/CoordTermPeriods';
import CoordTermSubjectSplit  from './pages/CoordTermSubjectSplit';
import FinanceMembers         from './pages/FinanceMembers';
import ParentManagement       from './pages/ParentManagement';

// ── Library / Hostel modules ──────────────────────────────────────────────────
import LibrarianLayout from './components/Layout/LibrarianLayout';
import HostelLayout    from './components/Layout/HostelLayout';
import {
  LibrarianDashboard, LibrarianProfile, LibrarianMyAttendance,
  LibrarianMyLeaves, LibrarianMySalary,
} from './pages/LibrarianPages';
import { LibraryCatalog, LibraryIssueReturn, LibraryFines } from './pages/LibraryPages';
import { HostelRooms, HostelAllocation, HostelFines } from './pages/HostelOpsPages';
import {
  HostelDashboard, HostelProfile, HostelMyAttendance,
  HostelMyLeaves, HostelMySalary,
} from './pages/HostelPages';
import LibrariansAdmin    from './pages/LibrariansAdmin';
import HostelMembersAdmin from './pages/HostelMembersAdmin';
import LibraryOverview    from './pages/LibraryOverview';
import HostelOverview     from './pages/HostelOverview';
import FinanceLibrarians    from './pages/FinanceLibrarians';
import FinanceHostelMembers from './pages/FinanceHostelMembers';

const AdminCertificates   = createCertificatesPage({ apiBase: '/admin' });
const FinanceCertificates = createCertificatesPage({ apiBase: '/finance' });

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : (
        <Navigate to={
          user.role === 'super-admin' ? '/super/schools' :
          user.role === 'teacher'     ? '/teacher/dashboard' :
          user.role === 'student'     ? '/student/dashboard' :
          user.role === 'coordinator'   ? '/coordinator/dashboard' :
          user.role === 'finance'       ? '/finance/dashboard'     :
          user.role === 'parent'        ? '/parent/dashboard'      :
          user.role === 'owner'         ? '/owner/dashboard'       :
          user.role === 'librarian'     ? '/librarian/dashboard'   :
          user.role === 'hostel_member' ? '/hostel/dashboard'      :
          '/dashboard'
        } replace />
      )} />

      {/* ── Super Admin Routes ─────────────────────────────────────────────── */}
      <Route
        path="/super"
        element={
          <PrivateRoute allowedRoles={['super-admin']}>
            <SuperLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="schools" />} />
        <Route path="dashboard"         element={<SuperDashboard />} />
        <Route path="schools"           element={<SuperSchools />} />
        <Route path="schools/:schoolId" element={<SuperSchoolDetail />} />
        <Route path="students"          element={<SuperStudents />} />
        <Route path="teachers"          element={<SuperTeachers />} />
        <Route path="admins"            element={<SuperAdmins />} />
        <Route path="owners"            element={<SuperOwners />} />
        <Route path="staff"             element={<SuperStaff />} />
        <Route path="subscriptions"     element={<SuperSubscriptions />} />
        <Route path="services"          element={<SuperServices />} />
        <Route path="billing"           element={<SuperBilling />} />
        <Route path="branch-requests"   element={<SuperBranchRequests />} />
      </Route>

      {/* ── Owner Routes ─────────────────────────────────────────────────── */}
      <Route
        path="/owner"
        element={
          <PrivateRoute allowedRoles={['owner']}>
            <OwnerLayout />
          </PrivateRoute>
        }
      >
        <Route index                element={<Navigate to="dashboard" />} />
        <Route path="dashboard"     element={<OwnerDashboard />} />
        <Route path="branches"      element={<OwnerBranches />} />
        <Route path="branches/:schoolId/access" element={<OwnerAdminAccess />} />
        <Route path="add-branch"    element={<OwnerAddBranch />} />
        <Route path="settings"                                          element={<OwnerDashboard />} />
        <Route path="admin-attendance"                                  element={<OwnerAdminAttendance />} />
        <Route path="admin-salary"                                      element={<OwnerAdminSalary />} />
        <Route path="admin-salary/:schoolId/:teacherId"                 element={<OwnerAdminSalaryYear />} />
        <Route path="admin-salary/:schoolId/:teacherId/:month"          element={<OwnerAdminSalaryMonth />} />
        <Route path="admin-leaves"                                      element={<OwnerAdminLeaves />} />
        <Route path="notices"                                           element={<OwnerNotices />} />
      </Route>

      {/* ── School Admin Routes ────────────────────────────────────────────── */}
      <Route
        path="/"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AccessProvider>
              <Layout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard"           element={<AccessGuard module="dashboard"    label="Dashboard"><Dashboard /></AccessGuard>} />
        <Route path="students"            element={<AccessGuard module="students"     label="Students"><Students /></AccessGuard>} />
        <Route path="students/:id/history" element={<AccessGuard module="students"    label="Students"><StudentHistory role="admin" /></AccessGuard>} />
        <Route path="student-left"        element={<AccessGuard module="studentLeft"  label="Left Students"><StudentLeft /></AccessGuard>} />
        <Route path="teachers"            element={<AccessGuard module="teachers"     label="Teachers"><Teachers /></AccessGuard>} />
        <Route path="staff-history/:id"   element={<AccessGuard module="teachers"     label="Staff History"><StaffHistory role="admin" /></AccessGuard>} />
        <Route path="staff-exits"         element={<AccessGuard module="staffExits" label="Staff Exits"><StaffExits /></AccessGuard>} />
        <Route path="classes"             element={<AccessGuard module="classes"      label="Classes"><Classes /></AccessGuard>} />
        <Route path="fees"                element={<AccessGuard module="fees"         label="Fees"><Fees /></AccessGuard>} />
        <Route path="salary-records"                   element={<AccessGuard module="salaryRecords" label="Salary Records"><SalaryRecords /></AccessGuard>} />
        <Route path="salary-records/:staffId"          element={<AccessGuard module="salaryRecords" label="Salary Records"><StaffSalaryYear /></AccessGuard>} />
        <Route path="salary-records/:staffId/:month"   element={<AccessGuard module="salaryRecords" label="Salary Records"><StaffSalaryMonth /></AccessGuard>} />
        <Route path="exams"               element={<Exams />} />
        <Route path="materials"           element={<AccessGuard module="materials"    label="Materials"><Materials /></AccessGuard>} />
        <Route path="notices"             element={<AccessGuard module="notices"      label="Notices"><Notices /></AccessGuard>} />
        <Route path="settings"            element={<AccessGuard module="settings"     label="Settings"><Settings /></AccessGuard>} />
        <Route path="leaves"              element={<AccessGuard module="leaves"       label="Leaves"><LeaveManagement /></AccessGuard>} />
        <Route path="biometric"           element={<AccessGuard module="biometric"    label="Biometric"><BiometricSettings /></AccessGuard>} />
        <Route path="teacher-attendance"  element={<AccessGuard module="teacherAttendance" label="Teacher Attendance"><TeacherAttendancePage /></AccessGuard>} />
        <Route path="timetable"           element={<AccessGuard module="timetable"        label="Timetable"><Timetable /></AccessGuard>} />
        <Route path="teacher-timetable"   element={<AccessGuard module="teacherTimetable" label="Teacher Timetable"><TeacherTimetable /></AccessGuard>} />
        <Route path="assessment-types"    element={<AccessGuard module="assessmentTypes"  label="Assessment Types"><AssessmentTypes /></AccessGuard>} />
        <Route path="term-periods"        element={<AccessGuard module="assessmentTypes"  label="Terms"><TermPeriods /></AccessGuard>} />
        <Route path="term-subject-split"  element={<AccessGuard module="assessmentTypes"  label="Subject Split"><TermSubjectSplit /></AccessGuard>} />
        <Route path="term-exams"          element={<AccessGuard module="assessments"      label="Term Exams"><AdminTermExams /></AccessGuard>} />
        <Route path="assessments"         element={<AccessGuard module="assessments"      label="Assessments"><AdminAssessments /></AccessGuard>} />
        <Route path="class-promotion"     element={<AccessGuard module="classPromotion"   label="Class Promotion"><ClassPromotion /></AccessGuard>} />
        <Route path="year-end-cleanup"    element={<AccessGuard module="yearEndCleanup"   label="Year-End Cleanup"><YearEndCleanup /></AccessGuard>} />
        <Route path="clo-management"      element={<AccessGuard module="cloManagement"    label="CLO Management"><CLOManagement /></AccessGuard>} />
        <Route path="attainment"          element={<AccessGuard module="attainment"       label="Attainment"><AttainmentDashboard role="admin" /></AccessGuard>} />
        <Route path="plo-management"       element={<AccessGuard module="ploManagement"   label="PLO Management"><PLOManagement /></AccessGuard>} />
        <Route path="plo-attainment"       element={<AccessGuard module="ploAttainment"   label="PLO Attainment"><PLOAttainmentDashboard role="admin" /></AccessGuard>} />
        <Route path="feedback"             element={<AccessGuard module="feedback"        label="Feedback"><AdminFeedback /></AccessGuard>} />
        <Route path="student-attendance"  element={<AccessGuard module="studentAttendance" label="Student Attendance"><AdminStudentAttendance /></AccessGuard>} />
        <Route path="staff-attendance"    element={<AccessGuard module="staffAttendance"   label="Staff Attendance"><StaffAttendance /></AccessGuard>} />
        <Route path="staff-salary"                        element={<AccessGuard module="staffSalary" label="Staff Salary"><StaffSalary /></AccessGuard>} />
        <Route path="staff-salary/:staffId"               element={<AccessGuard module="staffSalary" label="Staff Salary"><StaffSalaryYear /></AccessGuard>} />
        <Route path="staff-salary/:staffId/:month"        element={<AccessGuard module="staffSalary" label="Staff Salary"><StaffSalaryMonth /></AccessGuard>} />
        <Route path="staff-leaves"        element={<AccessGuard module="staffLeaves"     label="Staff Leaves"><StaffLeaves /></AccessGuard>} />
        <Route path="staff-management"    element={<AccessGuard module="staffManagement" label="Staff Management"><StaffManagement /></AccessGuard>} />
        <Route path="holidays"            element={<AccessGuard module="holidays"        label="Holidays"><Holidays /></AccessGuard>} />
        <Route path="datesheets"          element={<AccessGuard module="datesheets"      label="Datesheets"><AdminDatesheet /></AccessGuard>} />
        <Route path="expenses"            element={<AccessGuard module="expenses"        label="Expenses"><Expenses /></AccessGuard>} />
        <Route path="assets"              element={<AccessGuard module="assets"          label="Assets"><Assets /></AccessGuard>} />
        <Route path="certificates"        element={<AccessGuard module="certificates"    label="Certificates"><AdminCertificates /></AccessGuard>} />
        <Route path="access"              element={<AccessGuard module="accessPanel"     label="Access Panel"><AccessPanel /></AccessGuard>} />
        <Route path="coordinator-requests" element={<AccessGuard module="coordinatorRequests" label="Coordinator Requests"><CoordinatorRequestsAdmin /></AccessGuard>} />
        <Route path="finance-requests"     element={<AccessGuard module="financeRequests"     label="Finance Requests"><FinanceRequestsAdmin /></AccessGuard>} />
        <Route path="student-gate-log"     element={<AccessGuard module="studentGateLog"      label="Student Gate Log"><StudentGateLog /></AccessGuard>} />
        <Route path="my-attendance"        element={<AccessGuard module="myAttendance"        label="My Attendance"><AdminAttendance /></AccessGuard>} />
        <Route path="my-salary"            element={<AccessGuard module="mySalary"            label="My Salary"><AdminSalary /></AccessGuard>} />
        <Route path="my-leaves"            element={<AccessGuard module="myLeaves"            label="My Leaves"><AdminLeaves /></AccessGuard>} />
        <Route path="coordinators"         element={<AccessGuard module="coordinators"        label="Coordinators"><CoordinatorsAdmin /></AccessGuard>} />
        <Route path="finance-members"      element={<AccessGuard module="financeMembers"      label="Finance Members"><FinanceMembers /></AccessGuard>} />
        <Route path="librarians"           element={<AccessGuard module="librarians"          label="Librarians"><LibrariansAdmin /></AccessGuard>} />
        <Route path="hostel-members"       element={<AccessGuard module="hostelMembers"       label="Hostel Members"><HostelMembersAdmin /></AccessGuard>} />
        <Route path="library"              element={<AccessGuard module="library"             label="Library"><LibraryOverview role="admin" /></AccessGuard>} />
        <Route path="hostel-overview"      element={<AccessGuard module="hostel"              label="Hostel"><HostelOverview role="admin" /></AccessGuard>} />
        <Route path="parent-accounts"      element={<AccessGuard module="parentAccounts"      label="Parent Accounts"><ParentManagement /></AccessGuard>} />
        <Route path="ilp"                  element={<AccessGuard module="ilp"                 label="ILP"><AdminILP /></AccessGuard>} />
        <Route path="grade-reports"         element={<AccessGuard module="gradeReports"        label="Grade Reports"><GradeReport role="admin" /></AccessGuard>} />
      </Route>

      {/* ── Student Routes ─────────────────────────────────────────────────── */}
      <Route
        path="/student"
        element={
          <PrivateRoute allowedRoles={['student']}>
            <AccessProvider>
              <StudentLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"  element={<AccessGuard module="dashboard" label="Dashboard"><StudentDashboard /></AccessGuard>} />
        <Route path="profile"    element={<StudentProfile />} />
        <Route path="attendance" element={<AccessGuard module="attendance" label="Attendance"><StudentAttendance /></AccessGuard>} />
        <Route path="results"    element={<AccessGuard module="results"    label="Results"><StudentResults /></AccessGuard>} />
        <Route path="grade-report" element={<AccessGuard module="results" label="Grade Report"><StudentGradeReport /></AccessGuard>} />
        <Route path="fees"       element={<AccessGuard module="fees"       label="Fees"><StudentFees /></AccessGuard>} />
        <Route path="quizzes"    element={<AccessGuard module="quizzes"    label="Online Assessments"><StudentQuiz /></AccessGuard>} />
        <Route path="materials"  element={<AccessGuard module="materials"  label="Materials"><StudentMaterials /></AccessGuard>} />
        <Route path="notices"    element={<AccessGuard module="notices"    label="Notices"><StudentNotices /></AccessGuard>} />
        <Route path="timetable"  element={<AccessGuard module="timetable"  label="Timetable"><StudentTimetable /></AccessGuard>} />
        <Route path="datesheets" element={<AccessGuard module="datesheets" label="Datesheets"><StudentDatesheets /></AccessGuard>} />
        <Route path="feedback"    element={<StudentFeedback />} />
      </Route>

      {/* ── Teacher Routes ─────────────────────────────────────────────────── */}
      <Route
        path="/teacher"
        element={
          <PrivateRoute allowedRoles={['teacher']}>
            <AccessProvider>
              <TeacherLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard" element={<AccessGuard module="dashboard" label="Dashboard"><TeacherDashboard /></AccessGuard>} />
        <Route path="profile"   element={<TeacherProfile />} />
        <Route path="exams"     element={<TeacherExams />} />
        <Route path="classes"            element={<AccessGuard module="classes"             label="My Classes"><TeacherClasses /></AccessGuard>} />
        <Route path="attendance"         element={<AccessGuard module="attendance"          label="My Attendance"><TeacherAttendance /></AccessGuard>} />
        <Route path="salary"             element={<AccessGuard module="salary"              label="Salary"><TeacherSalary /></AccessGuard>} />
        <Route path="leaves"             element={<AccessGuard module="leaves"              label="Leaves"><TeacherLeaves /></AccessGuard>} />
        <Route path="quizzes"            element={<AccessGuard module="quizzes"             label="AI Quizzes"><TeacherQuiz /></AccessGuard>} />
        <Route path="materials"          element={<AccessGuard module="materials"           label="Materials"><TeacherMaterials /></AccessGuard>} />
        <Route path="class-media"        element={<TeacherClassMedia />} />
        <Route path="notices"            element={<AccessGuard module="notices"             label="Notices"><TeacherNotices /></AccessGuard>} />
        <Route path="messages"           element={<AccessGuard module="messages"            label="Messages"><TeacherMessages /></AccessGuard>} />
        <Route path="assessments"        element={<AccessGuard module="assessments"         label="Assessments"><TeacherAssessments /></AccessGuard>} />
        <Route path="term-exams"         element={<AccessGuard module="assessments"         label="Term Exams"><TeacherTermExams /></AccessGuard>} />
        <Route path="timetable"          element={<TeacherTimetable />} />
        <Route path="grade-report"       element={<AccessGuard module="gradeReports"        label="Grade Reports"><GradeReport /></AccessGuard>} />
        <Route path="attainment"          element={<AttainmentDashboard role="teacher" />} />
        <Route path="plo-attainment"       element={<PLOAttainmentDashboard role="teacher" />} />
        <Route path="feedback"             element={<TeacherFeedbackResults />} />
        <Route path="student-attendance" element={<AccessGuard module="studentAttendance"   label="Student Attendance"><TeacherStudentAttendance /></AccessGuard>} />
        <Route path="student-leaves"     element={<AccessGuard module="studentAttendance"   label="Student Leaves"><TeacherStudentLeaves /></AccessGuard>} />
        <Route path="datesheets"         element={<AccessGuard module="datesheets"          label="Datesheets"><TeacherDatesheet /></AccessGuard>} />
        <Route path="ilp"                element={<TeacherILP />} />
      </Route>

      {/* ── Finance Routes ──────────────────────────────────────────────────── */}
      <Route
        path="/finance"
        element={
          <PrivateRoute allowedRoles={['finance']}>
            <AccessProvider>
              <FinanceLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"        element={<AccessGuard module="dashboard"        label="Dashboard"><FinanceDashboard /></AccessGuard>} />
        <Route path="attendance"       element={<AccessGuard module="attendance"       label="Attendance"><FinanceAttendance /></AccessGuard>} />
        <Route path="leaves"           element={<AccessGuard module="leaves"           label="Leaves"><FinanceLeave /></AccessGuard>} />
        <Route path="staff-attendance" element={<AccessGuard module="staffAttendance"  label="Staff Attendance"><FinanceStaffAttendance /></AccessGuard>} />
        <Route path="staff-leaves"     element={<AccessGuard module="staffLeaves"      label="Staff Leaves"><FinanceStaffLeaves /></AccessGuard>} />
        <Route path="staff-management" element={<AccessGuard module="staffManagement"  label="Staff Management"><FinanceStaffManagement /></AccessGuard>} />
        <Route path="my-salary"        element={<AccessGuard module="mySalary"         label="My Salary"><FinanceMySalary /></AccessGuard>} />
        <Route path="fees"             element={<AccessGuard module="fees"             label="Fees"><FinanceFees /></AccessGuard>} />
        <Route path="expenses"         element={<AccessGuard module="expenses"         label="Expenses"><FinanceExpenses /></AccessGuard>} />
        <Route path="assets"           element={<AccessGuard module="assets"           label="Assets"><FinanceAssets /></AccessGuard>} />
        <Route path="salary"                          element={<AccessGuard module="staffSalary" label="Staff Salary"><FinanceSalary /></AccessGuard>} />
        <Route path="salary/:teacherId"               element={<AccessGuard module="staffSalary" label="Staff Salary"><FinanceSalaryYear /></AccessGuard>} />
        <Route path="salary/:teacherId/:month"        element={<AccessGuard module="staffSalary" label="Staff Salary"><FinanceSalaryMonth /></AccessGuard>} />
        <Route path="certificates"     element={<AccessGuard module="certificates"     label="Certificates"><FinanceCertificates /></AccessGuard>} />
        <Route path="requests"         element={<AccessGuard module="requests"         label="My Requests"><FinanceRequests /></AccessGuard>} />
        <Route path="notices"          element={<AccessGuard module="notices"          label="Notices"><FinanceNotices /></AccessGuard>} />
        <Route path="profile"          element={<AccessGuard module="profile"          label="Profile"><FinanceProfile /></AccessGuard>} />
        <Route path="biometric"        element={<AccessGuard module="biometric"        label="Biometric & RFID"><FinanceBiometric /></AccessGuard>} />
        <Route path="student-gate-log" element={<AccessGuard module="studentGateLog"   label="Student Gate Log"><FinanceStudentGateLog /></AccessGuard>} />
        <Route path="librarians"       element={<AccessGuard module="librarians"       label="Librarians"><FinanceLibrarians /></AccessGuard>} />
        <Route path="hostel-members"   element={<AccessGuard module="hostelMembers"    label="Hostel Members"><FinanceHostelMembers /></AccessGuard>} />
        <Route path="library"          element={<AccessGuard module="library"          label="Library"><LibraryOverview role="finance" /></AccessGuard>} />
        <Route path="hostel-overview"  element={<AccessGuard module="hostel"           label="Hostel"><HostelOverview role="finance" /></AccessGuard>} />
      </Route>

      {/* ── Coordinator Routes ────────────────────────────────────────────────── */}
      <Route
        path="/coordinator"
        element={
          <PrivateRoute allowedRoles={['coordinator']}>
            <AccessProvider>
              <CoordinatorLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"          element={<AccessGuard module="dashboard"          label="Dashboard"><CoordinatorDashboard /></AccessGuard>} />
        <Route path="students"           element={<AccessGuard module="students"           label="Students"><CoordStudents /></AccessGuard>} />
        <Route path="students/:id/history" element={<AccessGuard module="students"         label="Students"><StudentHistory role="coordinator" /></AccessGuard>} />
        <Route path="teachers"           element={<AccessGuard module="teachers"           label="Teachers"><CoordTeachers /></AccessGuard>} />
        <Route path="parents"            element={<AccessGuard module="parents"            label="Parent Accounts"><CoordParentManagement /></AccessGuard>} />
        <Route path="staff-history/:id"  element={<AccessGuard module="teachers"           label="Staff History"><StaffHistory role="coordinator" /></AccessGuard>} />
        <Route path="classes"            element={<AccessGuard module="classes"            label="Classes"><CoordClasses /></AccessGuard>} />
        <Route path="student-attendance" element={<AccessGuard module="studentAttendance"  label="Student Attendance"><CoordStudentAttendance /></AccessGuard>} />
        <Route path="salary"                   element={<AccessGuard module="salary" label="Salary"><CoordSalary /></AccessGuard>} />
        <Route path="salary/:teacherId"        element={<AccessGuard module="salary" label="Salary"><CoordSalaryYear /></AccessGuard>} />
        <Route path="salary/:teacherId/:month" element={<AccessGuard module="salary" label="Salary"><CoordSalaryMonth /></AccessGuard>} />
        <Route path="materials"          element={<AccessGuard module="materials"          label="Materials"><CoordMaterials /></AccessGuard>} />
        <Route path="assessment-types"   element={<AccessGuard module="assessmentTypes"    label="Assessment Types"><CoordAssessmentTypes /></AccessGuard>} />
        <Route path="term-periods"       element={<AccessGuard module="assessmentTypes"    label="Terms"><CoordTermPeriods /></AccessGuard>} />
        <Route path="term-subject-split" element={<AccessGuard module="assessmentTypes"    label="Subject Split"><CoordTermSubjectSplit /></AccessGuard>} />
        <Route path="assessments"        element={<AccessGuard module="assessments"        label="Assessments"><CoordAssessments /></AccessGuard>} />
        <Route path="attainment"         element={<AccessGuard module="attainment"         label="Attainment"><AttainmentDashboard role="coordinator" /></AccessGuard>} />
        <Route path="plo-management"       element={<AccessGuard module="ploManagement"    label="PLO Management"><PLOManagement readOnly={true} /></AccessGuard>} />
        <Route path="plo-attainment"      element={<AccessGuard module="ploAttainment"     label="PLO Attainment"><PLOAttainmentDashboard role="coordinator" /></AccessGuard>} />
        <Route path="feedback"            element={<AccessGuard module="feedback"          label="Feedback"><AdminFeedback readOnly={true} /></AccessGuard>} />
        <Route path="clo-management"      element={<AccessGuard module="cloManagement"     label="CLO Management"><CoordCLOManagement /></AccessGuard>} />
        <Route path="timetable"          element={<AccessGuard module="timetable"          label="Timetable"><CoordinatorTimetable /></AccessGuard>} />
        <Route path="grade-report"       element={<AccessGuard module="gradeReports"       label="Grade Reports"><CoordGradeReport /></AccessGuard>} />
        <Route path="notices"            element={<AccessGuard module="notices"            label="Notices"><CoordinatorNotices /></AccessGuard>} />
        <Route path="requests"           element={<AccessGuard module="myRequests"         label="My Requests"><CoordinatorRequests /></AccessGuard>} />
        <Route path="access"             element={<AccessGuard module="accessPanel"        label="Access Panel"><CoordAccessPanel /></AccessGuard>} />
        <Route path="profile"            element={<AccessGuard module="profile"            label="Profile"><CoordinatorProfile /></AccessGuard>} />
        <Route path="own-attendance"     element={<AccessGuard module="myAttendance"       label="My Attendance"><CoordOwnAttendance /></AccessGuard>} />
        <Route path="own-leaves"         element={<AccessGuard module="myLeaves"           label="My Leaves"><CoordOwnLeave /></AccessGuard>} />
        <Route path="holidays"           element={<AccessGuard module="holidays"           label="Holidays"><CoordHolidays /></AccessGuard>} />
        <Route path="student-left"       element={<AccessGuard module="studentLeft"        label="Student Left"><CoordStudentLeft /></AccessGuard>} />
        <Route path="staff-exits"        element={<AccessGuard module="staffExits"         label="Staff Exits"><CoordStaffExits /></AccessGuard>} />
        <Route path="year-end-cleanup"   element={<AccessGuard module="yearEndCleanup"     label="Year-End Cleanup"><YearEndCleanup readOnly={true} /></AccessGuard>} />
        <Route path="class-promotion"    element={<AccessGuard module="classPromotion"     label="Class Promotion"><ClassPromotion readOnly={true} /></AccessGuard>} />
      </Route>

      {/* ── Librarian Routes ──────────────────────────────────────────────────── */}
      <Route
        path="/librarian"
        element={
          <PrivateRoute allowedRoles={['librarian']}>
            <AccessProvider>
              <LibrarianLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"       element={<AccessGuard module="dashboard"    label="Dashboard"><LibrarianDashboard /></AccessGuard>} />
        <Route path="catalog"         element={<AccessGuard module="catalog"      label="Book Catalog"><LibraryCatalog /></AccessGuard>} />
        <Route path="issue-return"    element={<AccessGuard module="issueReturn"  label="Issue / Return"><LibraryIssueReturn /></AccessGuard>} />
        <Route path="fines"           element={<AccessGuard module="fines"        label="Fines"><LibraryFines /></AccessGuard>} />
        <Route path="own-attendance"  element={<AccessGuard module="myAttendance" label="My Attendance"><LibrarianMyAttendance /></AccessGuard>} />
        <Route path="own-leaves"      element={<AccessGuard module="myLeaves"     label="My Leaves"><LibrarianMyLeaves /></AccessGuard>} />
        <Route path="salary"          element={<AccessGuard module="mySalary"     label="My Salary"><LibrarianMySalary /></AccessGuard>} />
        <Route path="profile"         element={<AccessGuard module="profile"      label="Profile"><LibrarianProfile /></AccessGuard>} />
      </Route>

      {/* ── Hostel Member Routes ──────────────────────────────────────────────── */}
      <Route
        path="/hostel"
        element={
          <PrivateRoute allowedRoles={['hostel_member']}>
            <AccessProvider>
              <HostelLayout key={user?._id} />
            </AccessProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"       element={<AccessGuard module="dashboard"    label="Dashboard"><HostelDashboard /></AccessGuard>} />
        <Route path="rooms"           element={<AccessGuard module="rooms"        label="Rooms"><HostelRooms /></AccessGuard>} />
        <Route path="allocation"      element={<AccessGuard module="allocations"  label="Allocation"><HostelAllocation /></AccessGuard>} />
        <Route path="fines"           element={<AccessGuard module="fines"        label="Fines"><HostelFines /></AccessGuard>} />
        <Route path="own-attendance"  element={<AccessGuard module="myAttendance" label="My Attendance"><HostelMyAttendance /></AccessGuard>} />
        <Route path="own-leaves"      element={<AccessGuard module="myLeaves"     label="My Leaves"><HostelMyLeaves /></AccessGuard>} />
        <Route path="salary"          element={<AccessGuard module="mySalary"     label="My Salary"><HostelMySalary /></AccessGuard>} />
        <Route path="profile"         element={<AccessGuard module="profile"      label="Profile"><HostelProfile /></AccessGuard>} />
      </Route>

      {/* ── Parent Routes ──────────────────────────────────────────────────────── */}
      <Route
        path="/parent"
        element={
          <PrivateRoute allowedRoles={['parent']}>
            <ParentLayout key={user?._id} />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" />} />
        <Route path="dashboard"  element={<ParentDashboard />} />
        <Route path="attendance" element={<ParentAttendance />} />
        <Route path="results"    element={<ParentResults />} />
        <Route path="grade-report" element={<ParentGradeReport />} />
        <Route path="fees"       element={<ParentFees />} />
        <Route path="materials"  element={<ParentMaterials />} />
        <Route path="class-media" element={<ParentClassMedia />} />
        <Route path="notices"    element={<ParentNotices />} />
        <Route path="messages"   element={<ParentMessages />} />
        <Route path="timetable"  element={<ParentTimetable />} />
        <Route path="datesheets" element={<ParentDatesheets />} />
        <Route path="gate-log"   element={<ParentGateLog />} />
        <Route path="quizzes"    element={<ParentQuizzes />} />
        <Route path="feedback"   element={<ParentFeedback />} />
        <Route path="ilp"        element={<ParentILP />} />
        <Route path="profile"    element={<ParentProfile />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" containerStyle={{ zIndex: 999999 }} toastOptions={{ style: { zIndex: 999999 } }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;